import {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { signInWithCustomToken, signOut, onAuthStateChanged } from "firebase/auth";
import { toast } from "sonner";

import { auth, applyPersistence } from "@/lib/firebase";
import { authApi, onSessionExpired, ApiError } from "@/lib/api";

/**
 * Administrator session state.
 *
 * Sign-in is a two-step exchange rather than a direct Firebase call. The browser
 * posts the password to our API, which checks the lockout ledger, verifies the
 * credential and returns a custom token; only then does the Firebase session
 * begin. Doing it this way is what makes account lockout real — if the browser
 * called Firebase directly, failed attempts would never reach the server and
 * could simply not be reported.
 */

const AuthContext = createContext(null);

/**
 * Does `role`'s permission list satisfy `permission`?
 * Mirrors the server's check in functions/lib/roles.js. It governs what the UI
 * renders, never what the user is allowed to do — the server decides that.
 */
function hasPermission(permissions, permission) {
  if (!permissions || !permissions.length) return false;
  if (permissions.includes("*")) return true;
  if (permissions.includes(permission)) return true;
  const resource = permission.split(".")[0];
  return permissions.includes(`${resource}.*`);
}

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [profile, setProfile] = useState(null);
  // `initialising` covers the first auth-state resolution. Rendering routes
  // before it settles would flash the login page at an already-signed-in user.
  const [initialising, setInitialising] = useState(true);
  const [signingIn, setSigningIn] = useState(false);

  const signingOutRef = useRef(false);

  const clearSession = useCallback(async (message) => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    try {
      await signOut(auth);
    } catch (_) {
      // Already gone; nothing to do.
    } finally {
      setProfile(null);
      setFirebaseUser(null);
      signingOutRef.current = false;
      if (message) toast.error(message);
    }
  }, []);

  /* -- Hydrate the profile whenever a Firebase session appears -------------- */

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (!user) {
        setProfile(null);
        setInitialising(false);
        return;
      }

      try {
        const { profile: loaded } = await authApi.me();
        setProfile(loaded);
      } catch (err) {
        // A Firebase session that our API refuses is not a usable session:
        // the account may have been suspended, deleted or had its role
        // revoked since the token was issued.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          await clearSession(
            err.isSessionExpired ? null : err.message
          );
        } else {
          setProfile(null);
        }
      } finally {
        setInitialising(false);
      }
    });

    return unsubscribe;
  }, [clearSession]);

  /* -- React to the server declaring the session over ----------------------- */

  useEffect(
    () =>
      onSessionExpired((err) => {
        clearSession(err.message || "Your session has expired. Please sign in again.");
      }),
    [clearSession]
  );

  /* -- Actions -------------------------------------------------------------- */

  const login = useCallback(async (email, password, remember) => {
    setSigningIn(true);
    try {
      // Persistence must be set before the sign-in call, or the choice applies
      // only from the next session onwards.
      await applyPersistence(Boolean(remember));

      const { customToken, profile: loaded } = await authApi.login(email, password);
      await signInWithCustomToken(auth, customToken);

      setProfile(loaded);
      return loaded;
    } finally {
      setSigningIn(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (_) {
      // Recording the sign-out is best-effort; never block the user leaving.
    }
    await clearSession();
    toast.success("Signed out");
  }, [clearSession]);

  const refreshProfile = useCallback(async () => {
    if (!auth.currentUser) return null;
    const { profile: loaded } = await authApi.me();
    setProfile(loaded);
    return loaded;
  }, []);

  const can = useCallback(
    (permission) => hasPermission(profile ? profile.permissions : null, permission),
    [profile]
  );

  const canAny = useCallback(
    (...permissions) => permissions.some((p) => hasPermission(profile ? profile.permissions : null, p)),
    [profile]
  );

  const value = useMemo(
    () => ({
      user: firebaseUser,
      profile,
      // Authenticated means both halves agree: Firebase has a session and our
      // API recognises the account.
      isAuthenticated: Boolean(firebaseUser && profile),
      initialising,
      signingIn,
      login,
      logout,
      refreshProfile,
      can,
      canAny,
    }),
    [firebaseUser, profile, initialising, signingIn, login, logout, refreshProfile, can, canAny]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider.");
  return context;
}

export { hasPermission };
